import React, { useState } from "react";
import { Button, Modal, Select, Spin, Typography } from "antd";
import { useAppMessage } from "../../hooks/useAppMessage";
import { CheckOutlined } from "@ant-design/icons";
import "../../styles/ClosedFeedback/ClosedLoopFeedbackModal.css";
import { ExcludedAPI } from "../../API/ExcludedApi";
import { useFlightModel } from "../../contexts/FlightModelContext";

const { Text, Title } = Typography;

function formatValue(val, suffix = "") {
  if (val === null || val === undefined || val === "") return "--";
  const numeric = Number(val);
  if (!Number.isNaN(numeric)) {
    if (Number.isInteger(numeric)) {
      return `${numeric}${suffix}`;
    }
    return `${Number(numeric.toFixed(3))}${suffix}`;
  }
  return `${val}${suffix}`;
}

function formatDate(value) {
  if (!value) return "--";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "--";
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();
  return `${day}-${month}-${year}`;
}

function formatDateTime(value) {
  if (!value) return "--";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "--";

  const dayMonthYear = formatDate(value);
  const time = parsedDate.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return `${dayMonthYear} ${time.toUpperCase()}`;
}

function renderDelta(delta, direction) {
  if (delta === null || delta === undefined || delta === "") return "--";
  const numeric = Number(delta);
  if (Number.isNaN(numeric)) return delta;

  let deltaClass = "clf-delta-neutral";
  if (direction === "improved") {
    deltaClass = "clf-delta-positive";
  } else if (direction === "degraded") {
    deltaClass = "clf-delta-negative";
  } else {
    // Fallback for payloads that don't return direction.
    deltaClass =
      numeric > 0 ? "clf-delta-positive" : numeric < 0 ? "clf-delta-negative" : "clf-delta-neutral";
  }

  const sign = numeric > 0 ? "+" : "";
  const deltaValue = Number.isInteger(numeric) ? `${numeric}` : `${Number(numeric.toFixed(3))}`;
  return <span className={`clf-delta ${deltaClass}`}>{`${sign}${deltaValue}%`}</span>;
}

export default function ClosedLoopFeedbackModal({
  open,
  onClose,
  record,
  onRemove,
}) {
    const { selectedFlightModel, refreshFlightModels } = useFlightModel();
  const [feedbackData, setFeedbackData] = useState(null);
    const [spinning, setSpinning] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [fileUploadOptions, setFileUploadOptions] = useState([]);
  const [selectedUploadId, setSelectedUploadId] = useState(null);




   const [messageApi, contextHolder] = useAppMessage();
   const messageToast = (type, message) => {
    messageApi.open({
      type: type,
      content: message,
      duration: 6,
    });
  };

  const chips = [
    { label: "Custom Initial", value: feedbackData?.header?.custom_initial},
    { label: "Custom Repeat", value: feedbackData?.header?.custom_repeat },
    {
      label: "MRBR Initial",
      value: feedbackData?.header?.mrbr_initial,
    },
    {
      label: "MRBR Repeat",
      value: feedbackData?.header?.mrbr_repeat,
    },
  ];

  const handleDateChange = (value) => {
    setSelectedUploadId(value);
    getClosedLoopCompareApi(value);

  };

  const metricRows = [
    {
      label: "Defect Count",
      before: compareData?.before?.defect_count ?? feedbackData?.before?.defect_count ?? record?.before_defect_count,
      after: compareData?.after?.defect_count ?? record?.defect_no,
      delta: compareData?.changes?.defect_count?.pct ?? record?.defect_change_percent,
      direction: compareData?.changes?.defect_count?.direction,
    },
    {
      label: "Compliance Count",
      before:
        compareData?.before?.compliance_count ?? feedbackData?.before?.compliance_count ?? record?.before_compliance_count,
      after: compareData?.after?.compliance_count ?? record?.compliance_count,
      delta: compareData?.changes?.compliance_count?.pct ?? record?.compliance_change_percent,
      direction: compareData?.changes?.compliance_count?.direction,
    },
    {
      label: "Raw Finding Rate",
      before:
        compareData?.before?.raw_finding_rate ?? feedbackData?.before?.raw_finding_rate ?? record?.before_raw_findings_rate,
      after: compareData?.after?.raw_finding_rate ?? record?.raw_findings_rate,
      delta:
        compareData?.changes?.raw_finding_rate?.pct ?? record?.raw_findings_rate_change_percent,
      direction: compareData?.changes?.raw_finding_rate?.direction,
      suffix: "%",
    },
    {
      label: "Yield",
      before: compareData?.before?.yield_percentage ?? feedbackData?.before?.yield_percentage ?? record?.before_yield_percentage,
      after: compareData?.after?.yield_percentage ?? record?.yield_percentage,
      delta: compareData?.changes?.yield_percentage?.pct ?? record?.yield_change_percent,
      direction: compareData?.changes?.yield_percentage?.direction,
      suffix: "%",
    },
  ];

  async function getClosedLoopFeedBackApi(){
    setSpinning(true);
    try{
      if (!open || !record) {
        return;
      }
      const escalationId = record?.escalation_id || record?.id;
      if (!escalationId) return;
        let flightModelToUse = selectedFlightModel;
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");
      return;
    }

      const config = flightModelToUse
        ? {
            params: {
              flight_model: selectedFlightModel,
            },
          }
        : {};

      const response = await ExcludedAPI.getClosedLoopFeedback(escalationId, config);
      if (response?.data?.status !== true) {
        return;
      }
      
      setFeedbackData(response.data);
      setCompareData(null);

      const uploads = Array.isArray(response.data?.available_uploads)
        ? response.data.available_uploads
        : [];

      setFileUploadOptions(uploads);
      setSelectedUploadId(uploads?.[0]?.upload_history_id ?? null);
      if (uploads?.[0]?.upload_history_id) {
        await getClosedLoopCompareApi(uploads[0].upload_history_id);
      }
    }catch(error){
      console.log("error",error);
      
    } finally {
      setSpinning(false);
    }
  }

  async function getClosedLoopCompareApi(uploadHistoryId) {
    
    try {
      
      const escalationId = record?.escalation_id || record?.id;
      if (!escalationId || !uploadHistoryId) {
        setCompareData(null);
        return;
      }    
    

      const config = selectedFlightModel
        ? {
            params: {
              flight_model: selectedFlightModel,
            },
          }
        : {};

      const response = await ExcludedAPI.getClosedLoopCompare(escalationId, uploadHistoryId, config);
      const comparePayload = response?.data?.data;
      if (comparePayload?.status === true) {
        setCompareData({
          before: comparePayload?.before || null,
          after: comparePayload?.after || null,
          changes: comparePayload?.changes || null,
        });
        return;
      }
      setCompareData(null);
    } catch (error) {
      console.log("error", error);
      setCompareData(null);
    }
  }

  async function handleRemove() {
    try {
      if (!record) return;
      setSpinning(true);
      let flightModelToUse = selectedFlightModel;

      if (!flightModelToUse) {
        const refreshed = await refreshFlightModels();
        flightModelToUse = refreshed?.selectedFlightModel || null;
      }

      if (!flightModelToUse) {
        messageToast("error", "No flight model selected.");
        return;
      }

      const escalationId = record?.escalation_id || record?.id;
      if (!escalationId) {
        messageToast("error", "Optimization id is missing.");
        return;
      }

    const payload = {
  ids: [escalationId], // must be array
  flight_model: flightModelToUse,
};
      const response = await ExcludedAPI.removeClosedLoopFeedback(payload);
      const result = response?.data
    
      

      if (result?.status === true) {
        messageToast("success", result?.message || "Removed successfully.");
        if (onRemove) {
          await onRemove(record);
        } else {
          onClose();
        }
        return;
      }
      messageToast("error", result?.message || "Unable to remove closed loop feedback.");
    } catch (error) {
      console.log("error", error);
      messageToast("error", "Internal Server Error");
    } finally {
      setSpinning(false);
    }
  }

  React.useEffect(() => {
    getClosedLoopFeedBackApi();
  }, [open, record, selectedFlightModel]);



  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={980}
      centered
      bodyStyle={{ padding: "10px 18px 12px" }}
      className="clf-modal"
      title={
        <Title level={2} className="clf-title">
          {`Closed loop feedback for Spec No. ${ feedbackData?.header?.spec_concat}`}
        </Title>
      }
    >
       {contextHolder}
        <Spin spinning={spinning}>
      <div className="clf-top-section">
      <div className="clf-summary-card">
        <div>
          <Text className="clf-label">Card No.</Text>
          <div>
            <Text strong className="clf-summary-value">
              {formatValue(feedbackData?.header?.card_no)}
            </Text>
          </div>
        </div>
        <div>
          <Text className="clf-label">Completed Date</Text>
          <div>
            <Text strong className="clf-summary-value">
              {formatDate(feedbackData?.header?.escalated_on)}
            </Text>
          </div>
        </div>
        <div>
          <Text className="clf-label">Total Effort (Hrs)</Text>
          <div>
            <Text strong className="clf-summary-value">
              {formatValue(feedbackData?.header?.total_effort_hours)}
            </Text>
          </div>
        </div>
        <div>
          <Text className="clf-label">FEC</Text>
          <div>
            <Text strong className="clf-summary-value">
              {formatValue(feedbackData?.header?.fec)}
            </Text>
          </div>
        </div>
      </div>

      <div className="clf-chips-wrapper">
        {chips.map((chip) => (
          <div key={chip.label} className="clf-chip">
            <Text className="clf-chip-label">
              {chip.label}
            </Text>
            <Text strong className="clf-chip-value">
              {formatValue(chip.value)}
            </Text>
          </div>
        ))}
      </div>
      <div className="clf-file-upload-wrapper">
        <span className="clf-file-upload-icon-wrapper">
          <CheckOutlined className="clf-file-upload-icon" />
        </span>

        <Text className="clf-file-upload-label">
          File Upload Date
        </Text>

        <Select
          value={selectedUploadId}
          onChange={handleDateChange}
          bordered={false}
          suffixIcon={<span className="clf-file-upload-select-suffix">▼</span>}
          style={{ width: "max-content" }}
          dropdownStyle={{
            borderRadius: "8px",
            width: "max-content",
            minWidth: 0,
          }}
          className="clf-file-upload-select"
        >
          {fileUploadOptions?.map((upload) => (
            <Select.Option key={upload.upload_history_id} value={upload.upload_history_id}>
              {formatDateTime(upload.uploaded_at)}
            </Select.Option>
          ))}
        </Select>
      </div>
      </div>

      <div className="clf-metrics-wrapper">
        <div className="clf-metrics-header-row">
          <div className="clf-cell clf-before-cell">
            <Text strong className="clf-before-header">
              Before
            </Text>
          </div>
          <div className="clf-cell">
            <Text strong className="clf-after-header">
              After
            </Text>
          </div>
          <div className="clf-cell" />
        </div>

        {metricRows.map((row) => (
          <div key={row.label} className="clf-metric-row">
            <div className="clf-cell clf-before-cell">
              <Text className="clf-label">{row.label}</Text>
              <div>
                <Text strong className="clf-before-value">
                  {formatValue(row.before, row.suffix)}
                </Text>
              </div>
            </div>
            <div className="clf-cell">
              <Text className="clf-label clf-after-label">{row.label}</Text>
              <div>
                <Text className="clf-after-value">{formatValue(row.after, row.suffix)}</Text>
              </div>
            </div>
            <div className="clf-cell clf-delta-cell">{renderDelta(row.delta, row.direction)}</div>
          </div>
        ))}
      </div>

      <div className="clf-button-wrapper">
        <Button
          type="primary"
          className="clf-remove-button"
          onClick={handleRemove}
        >
          Remove
        </Button>
      </div>
        </Spin>
    </Modal>
  );
}

